import { Injectable } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IdbService } from '../indexDB/idb.service';
import { LoggerService } from '../logger/logger.service';
import { RotationCheck, RotationCheckEntry } from 'src/app/models/RotationCheck';
import { RotationCheckPhoto } from 'src/app/models/RotationCheckPhoto';
import {
  RotationCheckReportOutcome,
  RotationCheckStagedReport,
} from 'src/app/models/RotationCheckReport';
import { dataUrlToBlob } from 'src/app/utils/data-url';
import { ResumableUploadService } from './resumable-upload.service';
import {
  RC_SITE_STORE,
  RC_ENTRY_STORE,
  RC_PHOTO_STORE,
} from './datasources/idb-rotation-check.datasource';

const SYNC_INTERVAL_MS = 30000;

const TOMBSTONE_KEY = 'rotationCheckPendingDeletes';

/**
 * Marks that the one-time re-drive of already-errored media has run on this
 * device. Versioned in the key so a later corpus repair gets its own pass.
 *
 * Bumped to v4 for the shared-iPad repair. Media captured by one crew member and
 * retried under the next person's session was errored by the server's ownership
 * check, and nothing retries an errored record. Those bytes are still on the
 * device. The pump now holds an item for the person who captured it instead of
 * erroring it, so putting that corpus back to 'pending' recovers it the next
 * time its author signs in on that iPad.
 */
const MEDIA_REDRIVE_KEY = 'rotationCheckMediaRedrive.v4';

/**
 * Record fields that exist only on this device. The server never sends them, so
 * a pulled record must not be allowed to erase them.
 *
 * This is a list rather than a two-line chain because the chain is what already
 * destroyed the local copy of a photo once: a field added later was simply not
 * in it. With `blob` on the record the same omission would destroy the only copy
 * of a gigabyte clip, so new device-only fields are covered by default.
 */
const DEVICE_ONLY_PHOTO_FIELDS: Array<keyof RotationCheckPhoto> = [
  'img',
  'thumb',
  'blob',
  'bytes',
  'storagePolicy',
  'objectName',
];

/**
 * How long a signed media URL from the server stays valid. Matches the window the
 * API signs for. Used only to derive an expiry when the payload does not state
 * one, so a stale URL can be refreshed instead of rendering as a broken image.
 */
const SIGNED_URL_TTL_MS = 480 * 60 * 1000;

/** Timestamp fields the API returns without a timezone designator. See asUtcIso(). */
const SERVER_DATE_FIELDS = [
  'createdDate',
  'submittedDate',
  'updatedDate',
  'safetyOverrideDate',
];

/** A deletion waiting to be pushed to the server. */
interface Tombstone {
  store: string;   // RC_SITE_STORE | RC_ENTRY_STORE | RC_PHOTO_STORE
  id: string;
}

/**
 * Background sync for the rotation-check module. The device is the source of
 * truth the UI reads/writes (IdbRotationCheckDataSource); this service is the
 * only thing that talks to the server, on a timer:
 *
 *   pushDeletes         — DELETE records the user removed locally (tombstones),
 *                         so a delete isn't resurrected by the next pull.
 *   pushSitesAndEntries — PUT locally-written sites then entries (parent before
 *                         child), then flag them 'synced'.
 *   media pump          — uploads captured media, OUTSIDE the cycle. See
 *                         kickMediaPump() for why it must not be awaited.
 *   pullAll             — fetch server sites/entries into the local stores,
 *                         unless a local pending edit / tombstone is still
 *                         outstanding.
 *
 * Writes go through IdbService.put (same path the datasource uses); this mirrors
 * how SyncService keeps jobFolders in step — it is the rotation-check
 * equivalent, not a second sync engine.
 *
 * Raw fetch() with an explicit ?sid is used (not Angular HttpClient) because the
 * HTTP interceptor would force Content-Type: application/json and break the
 * multipart photo upload.
 */
@Injectable({ providedIn: 'root' })
export class RotationCheckSyncService {
  private inflight: Promise<void> | null = null;
  private started = false;

  /**
   * Emits when one media record stops being 'pending' — it has either reached
   * the server or been refused for good.
   *
   * A screen reads its photo records once, when it opens, and holds that copy.
   * Nothing about an upload finishing in the background writes into it, so
   * without this a thumbnail would sit under a progress ring for the rest of the
   * visit even though the bytes had landed minutes earlier.
   */
  readonly mediaSettled$ = new Subject<{ referenceCode: string; syncStatus: 'synced' | 'error' }>();

  /** True while the media pump is walking the pending list. Concurrency limit of one. */
  private mediaPumpRunning = false;
  /** Reference codes currently being uploaded, so nothing is sent twice. */
  private mediaInFlight = new Set<string>();
  /**
   * Reference codes already reported as another user's, so the log says it once
   * rather than every thirty seconds for as long as the device holds the record.
   */
  private mediaOwnershipLogged = new Set<string>();

  constructor(
    private idb: IdbService,
    private logger: LoggerService,
    private uploads: ResumableUploadService
  ) {}

  /** Start the timer. Safe to call once at app startup; repeat calls are ignored. */
  init(): void {
    if (this.started) return;
    this.started = true;
    interval(SYNC_INTERVAL_MS).subscribe(() => this.run());
    this.run();
  }

  /**
   * One push+pull cycle. No-ops when signed out. Concurrent callers (the 30s
   * timer and an on-demand page-entry pull) are coalesced onto a single in-flight
   * cycle, and `run()` returns THAT promise — so a caller that awaits run() waits
   * for a real, completed push+pull instead of getting an instant no-op.
   */
  async run(): Promise<void> {
    if (this.inflight) return this.inflight;
    // Gate only on having a session — NOT on dl.online. That flag is set once at
    // startup by a single /api/server probe; if that probe ever fails it wedges
    // 'false' and silently kills all sync forever. Network failures below are
    // already classified + retried, so just attempt whenever we have a session.
    if (!this.sid()) return;
    this.inflight = this.cycle();
    try {
      await this.inflight;
    } finally {
      this.inflight = null;
    }
  }

  private async cycle(): Promise<void> {
    try {
      await this.pushDeletes();
      await this.pushSitesAndEntries();
      // Deliberately NOT awaited. See kickMediaPump().
      this.kickMediaPump();
      await this.pullAll();
    } catch (e) {
      this.logger.error('RotationCheck/sync', 'sync cycle failed', e);
    }
  }

  private get base(): string {
    return `${environment.apiServer}/api/rotation-check`;
  }

  private sid(): string {
    return localStorage.getItem('currentSessionID') || '';
  }

  /**
   * The signed-in user's numeric ID, or 0 when it cannot be read.
   *
   * Taken straight out of localStorage for the same reason the session ID is —
   * this service deliberately stays off the Angular plumbing so a sync pass can
   * never be held up by injection order. The session payload names the field
   * `internalContactsID`; `userID` covers an older shape of the same payload.
   * Zero means "not known" and is never treated as somebody's ID.
   */
  private currentUserId(): number {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return 0;
      const user = JSON.parse(raw);
      return Number(user?.internalContactsID ?? user?.userID) || 0;
    } catch {
      return 0;
    }
  }

  private q(url: string): string {
    return `${url}?sid=${encodeURIComponent(this.sid())}`;
  }

  /**
   * Classify an HTTP status for retry purposes:
   *   'ok'        — succeeded, settle locally.
   *   'retry'     — transient (offline/5xx/auth); leave 'pending', try next cycle.
   *   'terminal'  — client error the server will never accept (409/422/413/400);
   *                 mark 'error' and stop hammering it.
   */
  private classify(status: number): 'ok' | 'retry' | 'terminal' {
    if (status >= 200 && status < 300) return 'ok';
    // Auth, server errors, timeouts and throttling are transient — keep retrying.
    if (status === 401 || status === 403 || status === 408 || status === 425 || status === 429 || status >= 500) {
      return 'retry';
    }
    return 'terminal';
  }

  // ───── delete reconciliation ──────────────────────────────────────────

  /** Record a local deletion so the sync pushes it to the server (and pull won't resurrect it). */
  markDeleted(store: string, id: string): void {
    const list = this.tombstones();
    if (!list.some((t) => t.store === store && t.id === id)) {
      list.push({ store, id });
      localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(list));
    }
  }

  private tombstones(): Tombstone[] {
    try {
      return JSON.parse(localStorage.getItem(TOMBSTONE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  private hasTombstone(store: string, id: any): boolean {
    return this.tombstones().some((t) => t.store === store && t.id === id);
  }

  private clearTombstone(store: string, id: string): void {
    const list = this.tombstones().filter((t) => !(t.store === store && t.id === id));
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(list));
  }

  private deleteUrl(t: Tombstone): string | null {
    switch (t.store) {
      case RC_SITE_STORE: return `${this.base}/sites/${t.id}`;
      case RC_ENTRY_STORE: return `${this.base}/entries/${t.id}`;
      case RC_PHOTO_STORE: return `${this.base}/photos/${t.id}`;
      default: return null;
    }
  }

  private async pushDeletes(): Promise<void> {
    for (const t of this.tombstones()) {
      const url = this.deleteUrl(t);
      if (!url) { this.clearTombstone(t.store, t.id); continue; }
      try {
        const res = await fetch(this.q(url), { method: 'DELETE' });
        // 404 == already gone (idempotent); a terminal 4xx can't be retried away either.
        if (res.ok || res.status === 404 || this.classify(res.status) === 'terminal') {
          this.clearTombstone(t.store, t.id);
        }
      } catch (e) {
        this.logger.warn('RotationCheck/sync', 'delete push failed (will retry)', { url, error: String(e) });
      }
    }
  }

  // ───── push (device → server) ─────────────────────────────────────────

  private async pushSitesAndEntries(): Promise<void> {
    // FK order: sites first, then entries. Media is pushed separately by the
    // pump, which re-establishes the same guarantee with an explicit check —
    // see mediaMayBePushed().
    //
    // Read through the syncStatus index rather than scanning each store. This
    // runs on a 30-second timer, and the photo scan in particular was pulling
    // every stored photo — bytes included — out of IndexedDB twice a minute to
    // find the pending few. Harmless with small base64 stills, ruinous once a
    // record can carry a gigabyte video blob.
    const sites: RotationCheck[] =
      (await this.idb.getAllByIndex(RC_SITE_STORE, 'syncStatus', 'pending')) || [];
    for (const s of sites) {
      await this.pushJson(`${this.base}/sites/${s.rotationCheckID}`, s, RC_SITE_STORE);
    }
    const entries: RotationCheckEntry[] =
      (await this.idb.getAllByIndex(RC_ENTRY_STORE, 'syncStatus', 'pending')) || [];
    for (const e of entries) {
      await this.pushJson(`${this.base}/entries/${e.rotationCheckEntryID}`, e, RC_ENTRY_STORE);
    }
  }

  // ───── media pump (device → Google, outside the cycle) ────────────────

  /**
   * Start the media pump if it is not already running, and return immediately.
   *
   * **Not awaiting this is the point.** Uploads used to sit inside the same
   * serial push the cycle awaited, so a twenty-minute clip blocked deletes,
   * entry pushes and every pull for twenty minutes — map pins stopped
   * refreshing and the whole module looked frozen. The pump runs on its own,
   * one item at a time, and the metadata path no longer waits on it.
   */
  kickMediaPump(): void {
    if (this.mediaPumpRunning) return;
    if (!this.sid()) return;
    this.mediaPumpRunning = true;
    this.runMediaPump()
      .catch((e) => this.logger.error('RotationCheck/sync', 'media pump failed', e))
      .finally(() => {
        this.mediaPumpRunning = false;
      });
  }

  private async runMediaPump(): Promise<void> {
    await this.redriveErroredMediaOnce();

    const photos: RotationCheckPhoto[] =
      (await this.idb.getAllByIndex(RC_PHOTO_STORE, 'syncStatus', 'pending')) || [];

    for (const p of photos) {
      if (!p?.referenceCode) continue;
      // Claim first, ask questions after. The claim used to sit below the
      // parent check, which suspends — see claimMedia().
      if (!this.claimMedia(p.referenceCode)) continue;
      try {
        if (!(await this.mediaMayBePushed(p))) continue;
        await this.pushPhoto(p);
      } finally {
        this.mediaInFlight.delete(p.referenceCode);
      }
    }
  }

  /**
   * Take the in-flight marker for one item, or say that somebody else holds it.
   *
   * The test and the claim must not be separated by an `await`, and they used to
   * be: the pump read the marker, awaited an IndexedDB read, and only then added
   * itself. A foreground upload starting inside that gap passed its own test as
   * well, so both entered pushPhoto for the same record. The uploader's own
   * guard refused the second one, but whichever call finished first deleted the
   * shared marker while the other was still sending, which left the pump
   * re-entering an upload it could never win and let uploadNow report a running
   * upload as unfinished. Doing both in one synchronous step removes the gap —
   * JavaScript has nowhere to suspend between the two lines.
   */
  private claimMedia(referenceCode: string): boolean {
    if (this.mediaInFlight.has(referenceCode)) return false;
    this.mediaInFlight.add(referenceCode);
    return true;
  }

  /**
   * Whether this item may be sent right now. Two separate things say no, and
   * neither of them errors the record — it stays 'pending' and goes on a later
   * pass, with its bytes untouched.
   *
   * Its entry has to be on the server first. The pump runs independently of the
   * entry push, which removed the ordering the old serial push relied on.
   * Without this check, the first cycle after an offline submit would open an
   * upload session for a photo whose entry has not been pushed, get a 404 from
   * the server's entry check, and — because a 404 is a terminal status — mark
   * that record permanently errored with no way to retry it.
   *
   * The signed-in user also has to be the person who captured it. These iPads
   * are shared: a crew member can capture media, have the upload stop part-way
   * when the truck leaves coverage, sign out, and hand the device on. Nothing
   * about signing out clears IndexedDB, so the pump found the first person's
   * record still pending and retried it under the new session. The server
   * refuses an upload row owned by a different user with a 404, a 404 is
   * terminal, and the recording was thrown away. A photo carries no author of
   * its own, so the author of its parent entry stands in for it — the person who
   * wrote the entry is the person who captured its media. That field is on every
   * entry ever written, so this covers the records already sitting on devices
   * today and needs no migration.
   *
   * When either side is unknown the comparison is not made and the item is sent.
   * Refusing to send because identity could not be read would strand real media
   * on the device, which is the same loss by a different route.
   */
  private async mediaMayBePushed(p: RotationCheckPhoto): Promise<boolean> {
    const entryId = p.rotationCheckEntryID;
    if (!entryId || entryId === 'pending') return false;

    let entry: RotationCheckEntry;
    try {
      entry = await this.idb.select(RC_ENTRY_STORE, entryId);
    } catch {
      return false;
    }
    if (!entry) return false;
    if (entry.syncStatus === 'pending') return false;

    const signedIn = this.currentUserId();
    const capturedBy = Number(entry.createdByUserID) || 0;
    if (signedIn && capturedBy && signedIn !== capturedBy) {
      if (!this.mediaOwnershipLogged.has(p.referenceCode)) {
        this.mediaOwnershipLogged.add(p.referenceCode);
        this.logger.info('RotationCheck/sync', 'media held for the user who captured it', {
          ref: p.referenceCode,
          capturedBy,
          signedIn,
        });
      }
      return false;
    }

    return true;
  }

  /**
   * One-time pass that puts already-errored media back in the queue.
   *
   * Deleting the old size caps does not resurrect anything: a record the caps
   * refused was flagged 'error', the pump only picks up 'pending', and the
   * failed-media banner has no retry control. Every clip lost to those caps is
   * sitting in exactly that state right now, which is the corpus this work
   * exists to repair. Anything that still carries local bytes goes back to
   * 'pending'; anything that does not is beyond recovery and is left alone.
   *
   * The flag is only set after a clean pass, so a failure runs again next cycle
   * rather than skipping the repair permanently.
   */
  private async redriveErroredMediaOnce(): Promise<void> {
    if (localStorage.getItem(MEDIA_REDRIVE_KEY)) return;
    try {
      const errored: RotationCheckPhoto[] =
        (await this.idb.getAllByIndex(RC_PHOTO_STORE, 'syncStatus', 'error')) || [];
      let requeued = 0;
      for (const p of errored) {
        if (!p?.referenceCode) continue;
        if (!p.blob && !p.img) continue;
        await this.idb.put(RC_PHOTO_STORE, { ...p, syncStatus: 'pending' });
        requeued++;
      }
      localStorage.setItem(MEDIA_REDRIVE_KEY, new Date().toISOString());
      if (requeued) {
        this.logger.info('RotationCheck/sync', 'requeued media that the old size caps refused', {
          requeued,
        });
      }
    } catch (e) {
      this.logger.warn('RotationCheck/sync', 'media re-drive failed (will run again)', {
        error: String(e),
      });
    }
  }

  /**
   * Upload the media the caller names, in the foreground, after making sure
   * their entries are on the server first.
   *
   * Used by the no-room path: those items hold their only copy in memory, so
   * the person is kept on the screen until the upload finishes rather than
   * being allowed to walk away from bytes that exist nowhere else.
   */
  async uploadNow(referenceCodes: string[]): Promise<{ ok: string[]; pending: string[] }> {
    const ok: string[] = [];
    const pending: string[] = [];
    if (!this.sid()) return { ok, pending: [...referenceCodes] };

    try {
      await this.pushDeletes();
      await this.pushSitesAndEntries();
    } catch (e) {
      this.logger.warn('RotationCheck/sync', 'could not push before a foreground upload', {
        error: String(e),
      });
    }

    for (const ref of referenceCodes) {
      // Same reason as the pump: claim the marker before the first await, or
      // the pump can slip into the gap and both calls own the same upload.
      if (!this.claimMedia(ref)) { pending.push(ref); continue; }
      try {
        const p: RotationCheckPhoto = await this.idb.select(RC_PHOTO_STORE, ref);
        if (!p) { pending.push(ref); continue; }
        if (p.syncStatus === 'synced') { ok.push(ref); continue; }
        if (!(await this.mediaMayBePushed(p))) { pending.push(ref); continue; }
        await this.pushPhoto(p);
      } finally {
        this.mediaInFlight.delete(ref);
      }

      const after: RotationCheckPhoto = await this.idb.select(RC_PHOTO_STORE, ref);
      if (after?.syncStatus === 'synced') ok.push(ref);
      else pending.push(ref);
    }
    return { ok, pending };
  }

  /**
   * Stop and forget an upload because the record it belongs to is being
   * deleted. The partly written object is left for the server — a resumable
   * object is not visible in the bucket until its session finalises, and Google
   * discards an abandoned session by itself.
   */
  cancelMedia(referenceCode: string): void {
    this.uploads.cancel(referenceCode);
    this.uploads.forget(referenceCode);
  }

  /** PUT a site/entry as JSON. Settle 'synced' on success, 'error' on a terminal reject, leave 'pending' to retry. */
  private async pushJson(url: string, body: any, store: string): Promise<void> {
    try {
      const res = await fetch(this.q(url), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const verdict = this.classify(res.status);
      if (verdict === 'retry') {
        this.logger.warn('RotationCheck/sync', 'push transient failure (will retry)', { url, status: res.status });
        return; // leave 'pending'
      }
      if (verdict === 'terminal') {
        this.logger.warn('RotationCheck/sync', 'push rejected (marking error)', { url, status: res.status });
        await this.idb.put(store, { ...body, syncStatus: 'error' });
        return;
      }
      await this.idb.put(store, { ...body, syncStatus: 'synced' });
    } catch (e) {
      this.logger.warn('RotationCheck/sync', 'push failed (will retry)', { url, error: String(e) });
    }
  }

  /**
   * Upload one item's bytes, resumably and straight to Google.
   *
   * Every shape goes the same way. A record captured after v3 carries a `blob`;
   * one captured before it carries base64 in `img` and is converted here, so
   * legacy records get resumable upload with no migration. The converted blob
   * is deliberately not written back — that would double the record's size for
   * no gain.
   *
   * The multipart POST to /photos is gone from this path. It still exists on
   * the server so that an old app build cannot destroy a recording, but nothing
   * here uses it.
   */
  private async pushPhoto(p: RotationCheckPhoto): Promise<void> {
    const blob = await this.mediaBytesFor(p);
    if (!blob) return; // mediaBytesFor has already settled the record

    let outcome;
    try {
      outcome = await this.uploads.upload(p, blob);
    } catch (e) {
      this.logger.warn('RotationCheck/sync', 'media upload threw (will retry)', {
        ref: p.referenceCode,
        error: String(e),
      });
      return;
    }

    if (outcome.result === 'retry') {
      this.logger.warn('RotationCheck/sync', 'media upload unfinished (will retry)', {
        ref: p.referenceCode,
        reason: outcome.reason,
      });
      return; // leave 'pending'
    }

    if (outcome.result === 'terminal') {
      this.logger.warn('RotationCheck/sync', 'media upload rejected (marking error)', {
        ref: p.referenceCode,
        reason: outcome.reason,
      });
      await this.idb.put(RC_PHOTO_STORE, { ...p, syncStatus: 'error' });
      this.mediaSettled$.next({ referenceCode: p.referenceCode, syncStatus: 'error' });
      this.uploads.forget(p.referenceCode);
      return;
    }

    const saved = outcome.photo;
    const merged: RotationCheckPhoto = {
      ...p,
      url: saved?.url ?? p.url,
      thumbUrl: saved?.thumbUrl ?? p.thumbUrl,
      urlExpiresAt: new Date(Date.now() + SIGNED_URL_TTL_MS).toISOString(),
      syncStatus: 'synced',
    };
    // An upload-only item never had its bytes on disk and must not gain them
    // now — the device had no room, which is why it took this path at all.
    if (p.storagePolicy === 'upload-only') delete merged.blob;
    await this.idb.put(RC_PHOTO_STORE, merged);
    this.mediaSettled$.next({ referenceCode: p.referenceCode, syncStatus: 'synced' });
    this.uploads.forget(p.referenceCode);
  }

  /**
   * The bytes to send for one record, or null when there is nothing to send —
   * in which case this settles the record's flag itself, because each of those
   * cases means something different.
   */
  private async mediaBytesFor(p: RotationCheckPhoto): Promise<Blob | null> {
    if (p.blob) return p.blob;

    // An upload-only item keeps its only copy in memory.
    const held = this.uploads.held(p.referenceCode);
    if (held) return held;

    if (p.img) {
      const converted = dataUrlToBlob(p.img);
      if (converted) return converted;
      this.logger.warn('RotationCheck/sync', 'stored media is unreadable (marking error)', {
        ref: p.referenceCode,
      });
      await this.idb.put(RC_PHOTO_STORE, { ...p, syncStatus: 'error' });
      this.mediaSettled$.next({ referenceCode: p.referenceCode, syncStatus: 'error' });
      return null;
    }

    if (p.storagePolicy === 'upload-only') {
      // The app was closed part-way through. The bytes were never written to
      // the database and the in-memory copy went with the process, so there is
      // nothing left to send. Mark it errored so it shows in the failed-media
      // banner rather than sitting pending forever, claiming it will upload.
      this.logger.warn('RotationCheck/sync', 'upload-only media lost before it finished uploading', {
        ref: p.referenceCode,
        mediaType: p.mediaType,
      });
      await this.idb.put(RC_PHOTO_STORE, { ...p, syncStatus: 'error' });
      this.mediaSettled$.next({ referenceCode: p.referenceCode, syncStatus: 'error' });
      return null;
    }

    // No local bytes, and none were expected — already on the server.
    await this.idb.put(RC_PHOTO_STORE, { ...p, syncStatus: 'synced' });
    this.mediaSettled$.next({ referenceCode: p.referenceCode, syncStatus: 'synced' });
    return null;
  }

  // ───── emailed report ─────────────────────────────────────────────────

  /**
   * Ask the API to email the full rotation history for one location.
   *
   * The request is never refused because media is still uploading. When
   * something is outstanding the server stores the request and sends it itself
   * once the media lands, and says so with `staged`. That matters because the
   * wait can be hours on a field connection, and this app cannot send anything
   * while it is closed — a report the device had to wait around for was a report
   * that mostly never went.
   *
   * `awaitedReferenceCodes` is the media this device is still holding. The
   * server cannot see an item that has no upload session open yet, so without
   * the list it could judge a request ready and send it before that item ever
   * arrived. Only items that are genuinely still queued belong in it: one the
   * server has already refused is never going to settle, and naming it would
   * hold the report until the request aged out a week later.
   *
   * The endpoint answers 200 even when the message was refused, because the
   * share row is created either way — so `sent` is the thing to read, and
   * `detail` carries whatever the mail path said verbatim. A non-2xx means the
   * request itself was rejected, which is a different failure.
   */
  async sendReport(
    siteId: string,
    to: string,
    awaitedReferenceCodes: string[] = []
  ): Promise<RotationCheckReportOutcome> {
    const res = await fetch(this.q(`${this.base}/sites/${siteId}/report`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, awaitedReferenceCodes }),
    });

    if (!res.ok) {
      const body = (await res.text().catch(() => '')) || '';
      throw new Error(readServerMessage(body) || `The server refused the request (${res.status}).`);
    }

    const payload = await res.json().catch(() => null);
    if (!payload) {
      throw new Error('The server sent no answer. The report may or may not have gone out.');
    }
    return {
      sent: !!payload.sent,
      staged: !!payload.staged,
      detail: payload.detail || '',
      shareUrl: payload.shareUrl,
      stagedReportId: payload.stagedReportId,
      outstandingMedia: Number(payload.outstandingMedia) || 0,
    };
  }

  /**
   * Report requests stored against one location, newest first, in every state
   * they reached.
   *
   * Null means the question could not be asked, which is not the same answer as
   * an empty list. A screen that treated a failed fetch as "nothing is waiting"
   * would tell a crew their queued report had gone away.
   */
  async listStagedReports(siteId: string): Promise<RotationCheckStagedReport[] | null> {
    const rows = await this.getJson<RotationCheckStagedReport[]>(
      this.q(`${this.base}/sites/${siteId}/report-requests`),
      8000
    );
    if (!rows) return null;
    return rows.map((r) => normalizeServerDates(r));
  }

  /**
   * Withdraw a stored request so it never sends.
   *
   * Only one still waiting can be withdrawn. The server answers 409 for a
   * request that is already going out or has gone, and that is worth telling
   * apart from a failure — the person needs to know the report was sent, not
   * that the button did not work.
   */
  async cancelStagedReport(stagedReportId: string): Promise<void> {
    const res = await fetch(this.q(`${this.base}/report-requests/${stagedReportId}`), {
      method: 'DELETE',
    });
    if (res.ok) return;
    if (res.status === 409) {
      throw new Error('That report has already been sent, so it cannot be withdrawn.');
    }
    const body = (await res.text().catch(() => '')) || '';
    throw new Error(readServerMessage(body) || `The server refused the request (${res.status}).`);
  }

  // ───── pull (server → device) ─────────────────────────────────────────

  private async pullAll(): Promise<void> {
    // Full pull every cycle — deliberately NO incremental `?since=` cursor.
    //
    // The cursor froze sync permanently: it only advanced on a *successful* pull,
    // but the incremental fetch routinely failed over the trans-Pacific link with
    // "TypeError: Failed to fetch". A failed pull left the cursor un-advanced AND
    // brought no records, so new sites/entries (including other users' map pins)
    // never arrived — the module looked stuck on its first-load snapshot until a
    // sign-out/in wiped IndexedDB and forced a full pull. Full pulls always
    // succeeded; only the incremental path failed.
    //
    // A cursorless full pull has no state to get stuck, re-pulls everything each
    // cycle (upsertFromServer skips local pending edits + tombstones), and
    // self-heals after any transient failure. The payload is small. (Revisit if
    // this ever grows to thousands of rows and the per-cycle fetch gets heavy.)
    const [sites, entries] = await Promise.all([
      this.getJson<RotationCheck[]>(this.q(`${this.base}/sites`)),
      this.getJson<RotationCheckEntry[]>(this.q(`${this.base}/entries`)),
    ]);
    if (sites) for (const s of sites) await this.upsertFromServer(RC_SITE_STORE, s.rotationCheckID, s);
    if (entries) for (const e of entries) await this.upsertFromServer(RC_ENTRY_STORE, e.rotationCheckEntryID, e);
    // Photos pulled on-demand (pullPhotosForEntry) — fetching every photo for
    // every entry each cycle is too heavy for a field LTE connection.
  }

  /**
   * Pull one entry's photos from the server into the local store. Called when an
   * entry is opened so photos appear on a device that didn't author them.
   * Best-effort: no-ops offline / signed out / on failure.
   */
  async pullPhotosForEntry(entryId: string): Promise<void> {
    if (!this.sid()) return;
    // Bounded: this runs while the user waits on the entry view, so a hung
    // socket must not stall it — fall back to whatever photos are already local.
    const photos = await this.getJson<RotationCheckPhoto[]>(this.q(`${this.base}/entries/${entryId}/photos`), 5000);
    if (!photos) return;
    for (const p of photos) await this.upsertFromServer(RC_PHOTO_STORE, p.referenceCode, p);
  }

  /** GET JSON from a fully-built URL (caller includes ?sid via q()). Optional abort timeout. */
  private async getJson<T>(url: string, timeoutMs?: number): Promise<T | null> {
    const controller = timeoutMs ? new AbortController() : undefined;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const res = await fetch(url, { signal: controller?.signal });
      if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
      return (await res.json()) as T;
    } catch (e) {
      this.logger.warn('RotationCheck/sync', 'pull failed', { url, error: String(e) });
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * Write a server record locally, unless a local edit is still pending for that
   * key (local wins until pushed) or it's locally tombstoned (don't resurrect a
   * delete that hasn't reached the server yet).
   */
  private async upsertFromServer(store: string, key: any, record: any): Promise<void> {
    if (this.hasTombstone(store, key)) return;
    const local = await this.idb.select(store, key);
    if (local && local.syncStatus === 'pending') return;

    const merged: any = { ...normalizeServerDates(record), syncStatus: 'synced' };

    // Record when the server's signed URL stops being valid. The server signs
    // for a fixed window and the device caches records indefinitely, so a URL
    // pulled today is dead tomorrow — which breaks stills as readily as video.
    // Prefer an expiry the payload states; otherwise derive one from receipt.
    // Self-corrects the moment the API starts sending the field.
    if (store === RC_PHOTO_STORE && merged.url) {
      if (!merged.urlExpiresAt) {
        merged.urlExpiresAt = new Date(Date.now() + SIGNED_URL_TTL_MS).toISOString();
      }
    }

    // The local bytes and everything about how they are held exist only on this
    // device; the server payload never carries them. Overwriting the whole
    // record threw that copy away on the first pull after upload, leaving only
    // the server URL — a signed link that expires in hours and, for videos
    // uploaded before this fix, pointing at an object typed as an image. That is
    // why a rotation video played right after capture and was dead when
    // reopened. With `blob` on the record the same mistake would destroy the
    // only copy of a gigabyte clip, so every device-only field is carried
    // forward from one list rather than by a chain someone has to remember to
    // extend.
    if (store === RC_PHOTO_STORE && local) {
      for (const field of DEVICE_ONLY_PHOTO_FIELDS) {
        if (merged[field] == null && local[field] != null) merged[field] = local[field];
      }
    }

    await this.idb.put(store, merged);
  }
}

/**
 * Pull a readable sentence out of an error body, which may be JSON or plain
 * text. Long bodies are dropped rather than shown — an HTML error page is not
 * something to put in front of a crew.
 */
function readServerMessage(body: string): string {
  const text = (body || '').trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text);
    const message = parsed?.message || parsed?.Message || parsed?.detail || parsed?.title;
    if (typeof message === 'string' && message.trim()) return message.trim();
  } catch {
    /* not JSON */
  }
  if (text.length > 300 || text.startsWith('<')) return '';
  return text;
}

/**
 * Re-tag the API's naive timestamps as UTC.
 *
 * The API stores these as UTC but returns them from a `datetime2` column, which
 * holds no timezone, so they arrive with no designator — "2026-07-28T14:52:00".
 * Every JavaScript consumer, Angular's `date` pipe included, reads an
 * unqualified ISO string as LOCAL time. That rendered an 07:52 Pacific rotation
 * check as 14:52, exactly the UTC offset.
 *
 * The stored values are correct; only the labelling was lost. Restoring the
 * designator on the way in fixes existing records as well as new ones, with no
 * data migration. Values that already carry a zone are left untouched, so this
 * becomes a no-op if the API is later changed to emit the designator itself.
 */
function normalizeServerDates<T extends { [k: string]: any }>(record: T): T {
  if (!record || typeof record !== 'object') return record;
  const out: any = { ...record };
  for (const field of SERVER_DATE_FIELDS) {
    if (field in out) out[field] = asUtcIso(out[field]);
  }
  return out;
}

/** Append 'Z' to an ISO date-time that carries no timezone. Anything else passes through. */
function asUtcIso(value: any): any {
  if (typeof value !== 'string' || !value) return value;
  // Already zoned — trailing 'Z' or a ±hh:mm / ±hhmm offset.
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return value;
  // Only touch things shaped like a date-time; bare dates and free text pass through.
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value)) return value;
  return `${value.replace(' ', 'T')}Z`;
}
